import { useCallback, useMemo } from 'react'
import { useCADPageStore } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { useOperationSteps } from '@/services/queries/operation_steps'
import type { RawAssemblyTree, Explosions } from '@/state'
import type { Step } from '@/services/queries/operation_steps/types'

export const useRefreshCADVisibility = () => {
  const { isLoading: isLoadingCadData, data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const operationStep = useCADPageStore((state) => state.operationStep)
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const explosions = useCADPageStore((state) => state.explosions)
  const isExplosionLinesEnabled = useCADPageStore(
    (state) => state.isExplosionLinesEnabled,
  )
  const showFullModel = useCADPageStore((state) => state.showFullModel)
  const hidePreviousOperations = useCADPageStore(
    (state) => state.hidePreviousOperations,
  )
  const rawAssemblyTree = useMemo(
    () => cadData.rawAssemblyTree,
    [cadData.rawAssemblyTree],
  )
  const gltf = useMemo(() => cadData.gltf, [cadData.gltf])

  const activeStep = useMemo(
    () =>
      operationStep ? steps.find((s) => s.id === operationStep.stepId) : null,
    [operationStep, steps],
  )

  const isLoading = isLoadingCadData || isLoadingSteps

  const refreshCADVisibility = useCallback(
    (
      overrides: Partial<{
        assemblyTree: RawAssemblyTree
        steps: Step[]
        hiddenParts: string[]
        activeStep?: Step | null
        explosions: Explosions
        isExplosionLinesEnabled: boolean
        hidePreviousOperations: boolean
        showFullModel: boolean
      }>,
    ) => {
      if (!gltf || !rawAssemblyTree) return

      gltf.setVisibilityForOperationSteps({
        assemblyTree: rawAssemblyTree,
        steps,
        hiddenParts,
        activeStep,
        explosions,
        isExplosionLinesEnabled,
        showFullModel,
        hidePreviousOperations,
        ...overrides,
      })
    },
    [
      activeStep,
      explosions,
      gltf,
      hiddenParts,
      hidePreviousOperations,
      isExplosionLinesEnabled,
      rawAssemblyTree,
      showFullModel,
      steps,
    ],
  )

  return { refreshCADVisibility, isLoading }
}
